/** Filtre « entreprises récemment créées » : paliers proposés à la création
 *  d'une campagne, et helpers d'affichage de l'ancienneté d'une fiche.
 *
 *  Les valeurs sont des nombres de JOURS — c'est ce que le backend attend
 *  (`anciennete_max_jours`). Chaîne vide = aucune limite.
 */
/** `label` = formulaire de campagne (large) · `short` = barre de filtres
 *  compacte de la liste Entreprises, où le libellé long est tronqué. */
export const ANCIENNETE_OPTIONS = [
  { value: "", label: "Toutes (sans limite d'ancienneté)", short: "Toute ancienneté" },
  { value: "7", label: "Créées depuis moins d'1 semaine", short: "Créées < 1 semaine" },
  { value: "14", label: "Créées depuis moins de 2 semaines", short: "Créées < 2 semaines" },
  { value: "21", label: "Créées depuis moins de 3 semaines", short: "Créées < 3 semaines" },
  { value: "30", label: "Créées depuis moins d'1 mois", short: "Créées < 1 mois" },
  { value: "60", label: "Créées depuis moins de 2 mois", short: "Créées < 2 mois" },
  { value: "90", label: "Créées depuis moins de 3 mois", short: "Créées < 3 mois" },
  { value: "180", label: "Créées depuis moins de 6 mois", short: "Créées < 6 mois" },
  { value: "365", label: "Créées depuis moins d'1 an", short: "Créées < 1 an" },
  { value: "730", label: "Créées depuis moins de 2 ans", short: "Créées < 2 ans" },
];

/** Tuiles du formulaire de campagne (handoff « Campagnes ») : deux groupes
 *  de paliers cliquables, plus une fenêtre personnalisée saisie en jours.
 *  `days: null` = sans limite. Les paliers intermédiaires du sélecteur de la
 *  liste Entreprises (60 j, 2 ans…) restent accessibles par la saisie libre. */
export const ANCIENNETE_TILES = [
  { title: "Créations récentes", options: [
    { label: "Sans limite", sub: "toutes", days: null },
    { label: "24 h", sub: "1 j", days: 1 },
    { label: "Cette semaine", sub: "7 j", days: 7 },
    { label: "2 semaines", sub: "14 j", days: 14 },
    { label: "3 semaines", sub: "21 j", days: 21 },
  ] },
  { title: "Fenêtres larges", options: [
    { label: "1 mois", sub: "30 j", days: 30 },
    { label: "3 mois", sub: "90 j", days: 90 },
    { label: "6 mois", sub: "180 j", days: 180 },
    { label: "1 an", sub: "365 j", days: 365 },
  ] },
];

/** Phrase de résumé du filtre d'ancienneté ("" ou null = aucun filtre). */
export function ancienneteSummary(days) {
  const n = Number(days);
  if (!days || !Number.isFinite(n) || n <= 0) {
    return "Aucun filtre : toutes les entreprises, quelle que soit leur date de création.";
  }
  const tile = ANCIENNETE_TILES.flatMap((g) => g.options).find((o) => o.days === n);
  if (tile && n === 1) return "Entreprises créées dans les dernières 24 heures.";
  if (tile) return `Entreprises créées depuis moins de ${tile.label.toLowerCase()}.`;
  return `Entreprises créées il y a moins de ${n} jour${n > 1 ? "s" : ""}.`;
}

/** Libellé court pour la carte de campagne ("< 1 mois"). */
export function ancienneteLabel(days) {
  if (!days) return null;
  const n = Number(days);
  if (n < 30) return `< ${Math.round(n / 7)} sem.`;
  if (n < 365) return `< ${Math.round(n / 30)} mois`;
  const ans = n / 365;
  return `< ${ans % 1 === 0 ? ans : ans.toFixed(1)} an${ans >= 2 ? "s" : ""}`;
}

/** Ancienneté d'une fiche, à partir de sa date de création ISO. */
export function ageFromDate(dateCreation) {
  if (!dateCreation) return null;
  const d = new Date(String(dateCreation).slice(0, 10));
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  return days < 0 ? null : days;
}

/** "3 ans et 2 mois", "8 mois", "12 jours" — lisible pour un commercial. */
export function formatAge(days) {
  if (days === null || days === undefined) return null;
  if (days < 31) return `${days} jour${days > 1 ? "s" : ""}`;
  const mois = Math.floor(days / 30.44);
  if (mois < 12) return `${mois} mois`;
  const ans = Math.floor(days / 365.25);
  const reste = Math.floor((days - ans * 365.25) / 30.44);
  return reste ? `${ans} an${ans > 1 ? "s" : ""} et ${reste} mois` : `${ans} an${ans > 1 ? "s" : ""}`;
}

/** Date de création formatée en français, ou null. */
export function formatDateCreation(dateCreation) {
  if (!dateCreation) return null;
  const d = new Date(String(dateCreation).slice(0, 10));
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

/** Une entreprise de moins de 6 mois mérite d'être signalée en prospection. */
export const isRecent = (days) => days !== null && days !== undefined && days <= 180;
